<script setup lang="ts">
/**
 * Мои задачи — макет 18:821: три чипа отбора, группы по проектам и строки со
 * сроком и пилюлей статуса.
 *
 * Список сводный: он единственный экран, где рядом стоят задачи разработки и
 * продаж. Поэтому строка показывает ключ с префиксом проекта, а группа — сам
 * проект: без него «214» и «1421» ничем не отличаются.
 *
 * Статус задачи — колонка её проекта, и колонки догружаются только для тех
 * проектов, которые в списке действительно есть.
 */
import { AppButton } from '@vinprop/design-system'
import { AlertTriangle, CheckSquare } from 'lucide-vue-next'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import FilterChip from '@/components/FilterChip.vue'
import TrackerEmptyState from '@/components/TrackerEmptyState.vue'
import TrackerToolbar from '@/components/TrackerToolbar.vue'
import IssueTypeIcon from '@/features/issues/components/IssueTypeIcon.vue'
import { formatDueDate } from '@/features/issues/format'
import {
  groupByProject,
  isOverdue,
  listMine,
  loadColumns,
  type MyFilter,
  type MyIssue,
  type MySortField,
} from '@/features/my/api'
import { useProjectsStore } from '@/features/projects/store'

import type { ProjectColumn } from '@/features/projects/api'

const { t, locale } = useI18n()
const projects = useProjectsStore()

const assigned = ref<MyIssue[]>([])
const watching = ref<MyIssue[]>([])
const columns = ref<Record<string, ProjectColumn[]>>({})
const filter = ref<MyFilter>('assigned')
const isLoading = ref(false)
const error = ref('')

/**
 * Отбор и порядок уходят на сервер: личный список у человека с пятью проектами
 * — это сотни задач, и клиентский отбор показывал бы «ничего не найдено» там,
 * где искомое просто не поместилось в страницу.
 */
const projectFilter = ref<string[]>([])
const statusFilter = ref<string[]>([])
const dueFilter = ref<string[]>([])
const sortBy = ref<MySortField>('due_date')

/** Окна срока: «на сегодня» и «на неделю» — два вопроса, которые задают чаще всего */
const DUE_WINDOWS: Record<string, number> = { today: 0, week: 7 }

const STATUS_CATEGORIES = [
  'STATUS_CATEGORY_OPEN',
  'STATUS_CATEGORY_IN_PROGRESS',
  'STATUS_CATEGORY_DONE',
] as const

const SORT_FIELDS: MySortField[] = ['due_date', 'priority', 'updated_at', 'key']

/** Наблюдаемые — те, что пришли с подпиской, но не назначены на меня. */
const watchedOnly = computed(() => {
  const mine = new Set(assigned.value.map((issue) => issue.id))

  return watching.value.filter((issue) => !mine.has(issue.id))
})

const overdue = computed(() => watching.value.filter((issue) => isOverdue(issue)))

const chips = computed(() => [
  { value: 'assigned' as const, label: t('my.chips.assigned'), count: assigned.value.length },
  { value: 'watching' as const, label: t('my.chips.watching'), count: watchedOnly.value.length },
  { value: 'overdue' as const, label: t('my.chips.overdue'), count: overdue.value.length },
])

const visible = computed(() => {
  if (filter.value === 'watching') return watchedOnly.value
  if (filter.value === 'overdue') return overdue.value

  return assigned.value
})

const groups = computed(() =>
  groupByProject(
    visible.value,
    projects.items.map((project) => project.key),
  ),
)

const projectName = (key: string) => {
  const project = projects.items.find((entry) => entry.key === key)

  return project ? `${project.name} · ${project.key}` : key
}

/** Что уходит на сервер вместе со списком */
const query = computed(() => ({
  projectIds: projectFilter.value,
  statusCategories: statusFilter.value as ('STATUS_CATEGORY_OPEN')[],
  dueBefore: dueBefore.value,
  sortBy: sortBy.value,
}))

/**
 * Граница срока — конец выбранного окна: «на сегодня» это всё, что должно быть
 * сделано до завтрашнего утра, а не до текущей минуты.
 */
const dueBefore = computed(() => {
  if (!dueFilter.value.length) return undefined

  const days = Math.max(...dueFilter.value.map((value) => DUE_WINDOWS[value] ?? 0))
  const now = new Date()
  const edge = new Date(now.getFullYear(), now.getMonth(), now.getDate() + days + 1)

  return edge.toISOString()
})

const projectOptions = computed(() =>
  projects.items.map((project) => ({ value: project.id, label: `${project.name} · ${project.key}` })),
)

const statusOptions = computed(() =>
  STATUS_CATEGORIES.map((value) => ({ value, label: t(`my.filters.status.${value}`) })),
)

const dueOptions = computed(() =>
  Object.keys(DUE_WINDOWS).map((value) => ({ value, label: t(`my.filters.due.${value}`) })),
)

const sortOptions = computed(() =>
  SORT_FIELDS.map((value) => ({ value, label: t(`my.sort.${value}`) })),
)

const projectColor = (key: string) =>
  projects.items.find((entry) => entry.key === key)?.color || 'var(--color-primary-light)'

const columnOf = (issue: MyIssue) =>
  columns.value[issue.projectId]?.find((column) => column.id === issue.columnId)

/** Срок: у ближних дней слова, у дальних — дата, как в макете. */
const dueLabel = (issue: MyIssue) => {
  if (!issue.dueDate) return '—'

  const due = new Date(issue.dueDate)
  const now = new Date()
  const days = Math.round(
    (new Date(due.getFullYear(), due.getMonth(), due.getDate()).getTime() -
      new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()) /
      86_400_000,
  )

  if (days === 0) return t('my.due.today')
  if (days === 1) return t('my.due.tomorrow')

  return formatDueDate(issue.dueDate, locale.value)
}

const load = async () => {
  isLoading.value = true
  error.value = ''

  try {
    // Два списка, а не один: «наблюдаю» приходит вместе с назначенными, и
    // отделить одно от другого можно только сравнив выдачи.
    const [mine, all] = await Promise.all([listMine(false, query.value), listMine(true, query.value)])

    assigned.value = mine
    watching.value = all
    columns.value = await loadColumns(all.map((issue) => issue.projectId))
  } catch {
    error.value = t('my.error')
  } finally {
    isLoading.value = false
  }
}

// Чип «назначено / наблюдаю / просрочено» разбирается по уже загруженным
// выдачам, а вот фильтры и порядок — довод для сервера: перезапрашиваем.
watch([projectFilter, statusFilter, dueFilter, sortBy], () => void load(), { deep: true })

onMounted(async () => {
  await projects.ensureLoaded()
  await load()
})
</script>

<template>
  <TrackerToolbar :breadcrumbs="[{ label: t('nav.myTasks') }]">
    <FilterChip
      v-model="projectFilter"
      :label="t('my.filters.project')"
      :options="projectOptions"
      :empty-text="t('toolbar.filters.empty')"
    />
    <FilterChip
      v-model="statusFilter"
      :label="t('my.filters.statusLabel')"
      :options="statusOptions"
      :empty-text="t('toolbar.filters.empty')"
    />
    <FilterChip
      v-model="dueFilter"
      :label="t('my.filters.dueLabel')"
      :options="dueOptions"
      :empty-text="t('toolbar.filters.empty')"
    />

    <!-- Порядок — выбор из одного значения: чип отдаёт последнее нажатое. -->
    <FilterChip
      :model-value="[sortBy]"
      :label="t('my.sort.label')"
      :options="sortOptions"
      :empty-text="t('toolbar.filters.empty')"
      @update:model-value="sortBy = ($event[$event.length - 1] as MySortField) ?? 'due_date'"
    />
  </TrackerToolbar>

  <div class="my">
    <div class="my__chips">
      <button
        v-for="chip in chips"
        :key="chip.value"
        type="button"
        class="my__chip"
        :class="{ 'my__chip--active': filter === chip.value }"
        :aria-pressed="filter === chip.value"
        @click="filter = chip.value"
      >
        {{ chip.label }} · {{ chip.count }}
      </button>
    </div>

    <div v-if="isLoading && !watching.length" class="my__skeleton" />

    <TrackerEmptyState
      v-else-if="error"
      :icon="AlertTriangle"
      :title="t('my.error')"
      :description="t('my.errorHint')"
      tint="var(--color-danger-tint)"
    >
      <AppButton variant="secondary" @click="load">{{ t('devops.envs.retry') }}</AppButton>
    </TrackerEmptyState>

    <TrackerEmptyState
      v-else-if="!groups.length"
      :icon="CheckSquare"
      :title="t(`my.empty.${filter}.title`)"
      :description="t(`my.empty.${filter}.description`)"
    />

    <template v-else>
      <section v-for="group in groups" :key="group.projectKey" class="my__group">
        <h2 class="my__group-head">
          <span class="my__dot" :style="{ backgroundColor: projectColor(group.projectKey) }" />
          {{ projectName(group.projectKey) }}
        </h2>

        <div class="my__table">
          <RouterLink
            v-for="issue in group.issues"
            :key="issue.id"
            class="mrow"
            :to="`/issue/${issue.key}`"
          >
            <IssueTypeIcon class="mrow__icon" :type="issue.type" />
            <span class="mrow__key">{{ issue.key }}</span>
            <span class="mrow__title">{{ issue.title }}</span>
            <span class="mrow__due" :class="{ 'mrow__due--overdue': isOverdue(issue) }">
              {{ dueLabel(issue) }}
            </span>
            <span
              v-if="columnOf(issue)"
              class="mrow__status"
              :style="{
                color: columnOf(issue)?.color,
                backgroundColor: `color-mix(in srgb, ${columnOf(issue)?.color} 12%, transparent)`,
              }"
            >
              {{ columnOf(issue)?.name }}
            </span>
          </RouterLink>
        </div>
      </section>
    </template>
  </div>
</template>

<style scoped>
.my {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 16px;
  padding: 28px 48px;
}

.my__chips {
  display: flex;
  gap: 8px;
}

.my__chip {
  height: 28px;
  padding: 0 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-full);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  font-family: inherit;
  font-size: var(--font-size-body-sm);
  cursor: pointer;
}

.my__chip--active {
  border-color: var(--color-primary);
  background: var(--color-primary-tint);
  color: var(--color-primary-hover);
}

.my__group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.my__group-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-overline);
  font-weight: var(--font-weight-regular);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.my__dot {
  width: 8px;
  height: 8px;
  border-radius: var(--radius-full);
}

.my__table {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  background: var(--color-surface);
  overflow: hidden;
}

.my__skeleton {
  height: 320px;
  border-radius: var(--radius-xl);
  background: var(--color-bg-muted);
}

.mrow {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 40px;
  padding: 0 16px;
  border-bottom: 1px solid var(--color-border);
  color: inherit;
  text-decoration: none;
}

.mrow:last-child {
  border-bottom: none;
}

.mrow:hover {
  background: var(--color-bg-muted);
}

.mrow__key {
  width: 88px;
  color: var(--color-text-primary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-body-sm);
}

.mrow__title {
  flex: 1;
  overflow: hidden;
  color: var(--color-text-secondary);
  font-size: var(--font-size-body);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.mrow__due {
  width: 76px;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
  text-align: right;
}

/* Просроченный срок красный: это единственное, ради чего на колонку смотрят. */
.mrow__due--overdue {
  color: var(--color-danger);
}

.mrow__status {
  min-width: 76px;
  padding: 2px 8px;
  border-radius: var(--radius-sm);
  font-size: var(--font-size-body-sm);
  text-align: center;
  white-space: nowrap;
}

/* Телефон — макет 28:107: строка становится двухстрочной карточкой. Ключ, срок
   и статус уходят наверх, название занимает вторую строку целиком: в 390
   пикселей одной строкой оно обрезается на третьем слове. */
@media (max-width: 768px) {
  .my {
    gap: 12px;
    padding: 16px 12px;
  }

  .my__chips {
    overflow-x: auto;
    margin: 0 -12px;
    padding: 0 12px;
    scrollbar-width: none;
  }

  .my__chip {
    flex-shrink: 0;
    height: 36px;
  }

  .mrow {
    display: grid;
    grid-template-areas:
      'icon key due status'
      'title title title title';
    grid-template-columns: auto auto 1fr auto;
    align-items: center;
    gap: 4px 8px;
    height: auto;
    padding: 12px 14px;
  }

  .mrow__icon {
    grid-area: icon;
  }

  .mrow__key {
    grid-area: key;
    width: auto;
  }

  .mrow__due {
    grid-area: due;
    width: auto;
    text-align: right;
  }

  .mrow__status {
    grid-area: status;
    min-width: 0;
  }

  .mrow__title {
    grid-area: title;
    white-space: normal;
  }
}
</style>
